import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit as limitQuery,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { 
  FacultySubjectAssignment, 
  FacultySubjectAssignmentFormData,
  BulkFacultyAssignmentFormData,
  User,
  Subject,
  Class,
  ClassSubject
} from '../types';

export class FacultySubjectService {
  private static collectionName = 'facultySubjectAssignments';

  // Create a new faculty-subject assignment
  static async createAssignment(data: FacultySubjectAssignmentFormData): Promise<string> {
    try {
      const db = getDB();
      // Get faculty and subject details for display
      const [facultyDoc, subjectDoc] = await Promise.all([
        getDoc(doc(db, 'users', data.facultyId)),
        getDoc(doc(db, 'subjects', data.subjectId))
      ]);

      if (!facultyDoc.exists()) {
        throw new Error('Faculty not found');
      }

      if (!subjectDoc.exists()) {
        throw new Error('Subject not found');
      }

      const faculty = { id: facultyDoc.id, ...facultyDoc.data() } as User;
      const subject = { id: subjectDoc.id, ...subjectDoc.data() } as Subject;

      // Get class names
      const classNames: string[] = [];
      for (const classId of data.classIds) {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        if (classDoc.exists()) {
          const classData = classDoc.data() as Class;
          classNames.push(classData.name);
        }
      }

      const assignment: Omit<FacultySubjectAssignment, 'id'> = {
        facultyId: data.facultyId,
        facultyName: `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim() || faculty.username,
        subjectId: data.subjectId,
        subjectName: subject.name,
        subjectCode: subject.code,
        classIds: data.classIds,
        classNames,
        academicYear: data.academicYear,
        semester: data.semester,
        isActive: true,
        assignedAt: new Date(),
        assignedBy: 'admin', // This should come from auth context
        assignedByName: 'Administrator'
      };

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...assignment,
        assignedAt: Timestamp.fromDate(assignment.assignedAt)
      });

      // Update class subjects
      await this.updateClassSubjects(data.classIds, data.subjectId, data.facultyId);

      return docRef.id;
    } catch (error) {
      console.error('Error creating faculty subject assignment:', error);
      throw new Error('Failed to create assignment');
    }
  }

  // Bulk create assignments
  static async createBulkAssignments(data: BulkFacultyAssignmentFormData): Promise<void> {
    try {
      const batch = writeBatch(getDB());

      // Get faculty details
      const facultyDoc = await getDoc(doc(getDB(), 'users', data.facultyId));
      if (!facultyDoc.exists()) {
        throw new Error('Faculty not found');
      }

      const faculty = { id: facultyDoc.id, ...facultyDoc.data() } as User;
      const facultyName = `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim() || faculty.username;

      for (const assignment of data.assignments) {
        // Get subject details
        const subjectDoc = await getDoc(doc(getDB(), 'subjects', assignment.subjectId));
        if (!subjectDoc.exists()) {
          continue; // Skip invalid subjects
        }

        const subject = { id: subjectDoc.id, ...subjectDoc.data() } as Subject;

        // Get class names
        const classNames: string[] = [];
        for (const classId of assignment.classIds) {
          const classDoc = await getDoc(doc(getDB(), 'classes', classId));
          if (classDoc.exists()) {
            const classData = classDoc.data() as Class;
            classNames.push(classData.name);
          }
        }

        const assignmentData: Omit<FacultySubjectAssignment, 'id'> = {
          facultyId: data.facultyId,
          facultyName,
          subjectId: assignment.subjectId,
          subjectName: subject.name,
          subjectCode: subject.code,
          classIds: assignment.classIds,
          classNames,
          academicYear: data.academicYear,
          semester: data.semester,
          isActive: true,
          assignedAt: new Date(),
          assignedBy: 'admin',
          assignedByName: 'Administrator'
        };

        const newDocRef = doc(collection(getDB(), this.collectionName));
        batch.set(newDocRef, {
          ...assignmentData,
          assignedAt: Timestamp.fromDate(assignmentData.assignedAt)
        });

        // Update class subjects (this will be done separately due to batch limitations)
      }

      await batch.commit();

      // Update class subjects for all assignments
      for (const assignment of data.assignments) {
        await this.updateClassSubjects(assignment.classIds, assignment.subjectId, data.facultyId);
      }
    } catch (error) {
      console.error('Error creating bulk assignments:', error);
      throw new Error('Failed to create bulk assignments');
    }
  }

  // Update class subjects when faculty is assigned
  private static async updateClassSubjects(classIds: string[], subjectId: string, facultyId: string): Promise<void> {
    try {
      const [subjectDoc, facultyDoc] = await Promise.all([
        getDoc(doc(getDB(), 'subjects', subjectId)),
        getDoc(doc(getDB(), 'users', facultyId))
      ]);

      if (!subjectDoc.exists() || !facultyDoc.exists()) {
        return;
      }

      const subject = { id: subjectDoc.id, ...subjectDoc.data() } as Subject;
      const faculty = { id: facultyDoc.id, ...facultyDoc.data() } as User;
      const facultyName = `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim() || faculty.username;

      for (const classId of classIds) {
        const classDocRef = doc(getDB(), 'classes', classId);
        const classDoc = await getDoc(classDocRef);
        
        if (classDoc.exists()) {
          const classData = classDoc.data() as Class;
          const existingSubjects = classData.subjects || [];
          
          // Check if subject already exists in class
          const existingSubjectIndex = existingSubjects.findIndex(s => s.subjectId === subjectId);
          
          const classSubject: ClassSubject = {
            subjectId,
            subjectName: subject.name,
            subjectCode: subject.code,
            facultyId,
            facultyName,
            isActive: true
          };

          if (existingSubjectIndex >= 0) {
            // Update existing subject
            existingSubjects[existingSubjectIndex] = classSubject;
          } else {
            // Add new subject
            existingSubjects.push(classSubject);
          }

          await updateDoc(classDocRef, {
            subjects: existingSubjects
          });
        }
      }
    } catch (error) {
      console.error('Error updating class subjects:', error);
    }
  }

  // Get all assignments
  static async getAssignments(limitNum: number = 50): Promise<{ assignments: FacultySubjectAssignment[], hasMore: boolean }> {
    try {
      const q = query(
        collection(getDB(), this.collectionName),
        orderBy('assignedAt', 'desc'),
        limitQuery(limitNum)
      );

      const querySnapshot = await getDocs(q);
      const assignments: FacultySubjectAssignment[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        assignments.push({
          id: doc.id,
          ...data,
          assignedAt: data.assignedAt?.toDate() || new Date(),
        } as FacultySubjectAssignment);
      });

      return {
        assignments,
        hasMore: querySnapshot.size === limitNum
      };
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw new Error('Failed to fetch assignments');
    }
  }

  // Get assignments by faculty
  static async getAssignmentsByFaculty(facultyId: string): Promise<FacultySubjectAssignment[]> {
    try {
      const q = query(
        collection(getDB(), this.collectionName),
        where('facultyId', '==', facultyId),
        where('isActive', '==', true),
        orderBy('assignedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const assignments: FacultySubjectAssignment[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        assignments.push({
          id: doc.id,
          ...data,
          assignedAt: data.assignedAt?.toDate() || new Date(),
        } as FacultySubjectAssignment);
      });

      return assignments;
    } catch (error) {
      console.error('Error fetching faculty assignments:', error);
      throw new Error('Failed to fetch faculty assignments');
    }
  }

  // Get assignments by subject
  static async getAssignmentsBySubject(subjectId: string): Promise<FacultySubjectAssignment[]> {
    try {
      const q = query(
        collection(getDB(), this.collectionName),
        where('subjectId', '==', subjectId),
        where('isActive', '==', true),
        orderBy('assignedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const assignments: FacultySubjectAssignment[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        assignments.push({
          id: doc.id,
          ...data,
          assignedAt: data.assignedAt?.toDate() || new Date(),
        } as FacultySubjectAssignment);
      });

      return assignments;
    } catch (error) {
      console.error('Error fetching subject assignments:', error);
      throw new Error('Failed to fetch subject assignments');
    }
  }

  // Get assignments by class
  static async getAssignmentsByClass(classId: string): Promise<FacultySubjectAssignment[]> {
    try {
      const q = query(
        collection(getDB(), this.collectionName),
        where('classIds', 'array-contains', classId),
        where('isActive', '==', true),
        orderBy('assignedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const assignments: FacultySubjectAssignment[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        assignments.push({
          id: doc.id,
          ...data,
          assignedAt: data.assignedAt?.toDate() || new Date(),
        } as FacultySubjectAssignment);
      });

      return assignments;
    } catch (error) {
      console.error('Error fetching class assignments:', error);
      throw new Error('Failed to fetch class assignments');
    }
  }

  // Update assignment
  static async updateAssignment(id: string, data: Partial<FacultySubjectAssignmentFormData>): Promise<void> {
    try {
      const docRef = doc(getDB(), this.collectionName, id);
      const updateData: any = {};

      if (data.classIds) {
        updateData.classIds = data.classIds;
        
        // Get class names
        const classNames: string[] = [];
        for (const classId of data.classIds) {
          const classDoc = await getDoc(doc(getDB(), 'classes', classId));
          if (classDoc.exists()) {
            const classData = classDoc.data() as Class;
            classNames.push(classData.name);
          }
        }
        updateData.classNames = classNames;
      }

      if (data.academicYear) updateData.academicYear = data.academicYear;
      if (data.semester) updateData.semester = data.semester;

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating assignment:', error);
      throw new Error('Failed to update assignment');
    }
  }

  // Delete assignment
  static async deleteAssignment(id: string): Promise<void> {
    try {
      // Get assignment details before deletion
      const assignmentDoc = await getDoc(doc(getDB(), this.collectionName, id));
      if (!assignmentDoc.exists()) {
        throw new Error('Assignment not found');
      }

      const assignment = assignmentDoc.data() as FacultySubjectAssignment;
      
      // Remove subject from classes
      await this.removeSubjectFromClasses(assignment.classIds, assignment.subjectId);
      
      // Delete the assignment
      await deleteDoc(doc(getDB(), this.collectionName, id));
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw new Error('Failed to delete assignment');
    }
  }

  // Remove subject from classes
  private static async removeSubjectFromClasses(classIds: string[], subjectId: string): Promise<void> {
    try {
      for (const classId of classIds) {
        const classDocRef = doc(getDB(), 'classes', classId);
        const classDoc = await getDoc(classDocRef);
        
        if (classDoc.exists()) {
          const classData = classDoc.data() as Class;
          const subjects = classData.subjects || [];
          
          // Remove the subject
          const updatedSubjects = subjects.filter(s => s.subjectId !== subjectId);
          
          await updateDoc(classDocRef, {
            subjects: updatedSubjects
          });
        }
      }
    } catch (error) {
      console.error('Error removing subject from classes:', error);
    }
  }

  // Toggle assignment status
  static async toggleAssignmentStatus(id: string): Promise<void> {
    try {
      const docRef = doc(getDB(), this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Assignment not found');
      }

      const currentStatus = docSnap.data().isActive;
      await updateDoc(docRef, {
        isActive: !currentStatus
      });
    } catch (error) {
      console.error('Error toggling assignment status:', error);
      throw new Error('Failed to toggle assignment status');
    }
  }

  // Search assignments
  static async searchAssignments(searchTerm: string): Promise<FacultySubjectAssignment[]> {
    try {
      // For now, we'll get all assignments and filter on client side
      // In production, you might want to use Algolia or similar for better search
      const { assignments } = await this.getAssignments(100);
      
      const filteredAssignments = assignments.filter(assignment => 
        assignment.facultyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.subjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.classNames?.some(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      return filteredAssignments;
    } catch (error) {
      console.error('Error searching assignments:', error);
      throw new Error('Failed to search assignments');
    }
  }

  // Get faculty workload (number of subjects and classes assigned)
  static async getFacultyWorkload(facultyId: string): Promise<{
    totalSubjects: number;
    totalClasses: number;
    assignments: FacultySubjectAssignment[];
  }> {
    try {
      const assignments = await this.getAssignmentsByFaculty(facultyId);
      const totalSubjects = new Set(assignments.map(a => a.subjectId)).size;
      const totalClasses = new Set(assignments.flatMap(a => a.classIds)).size;

      return {
        totalSubjects,
        totalClasses,
        assignments
      };
    } catch (error) {
      console.error('Error fetching faculty workload:', error);
      throw new Error('Failed to fetch faculty workload');
    }
  }
} 